import { Button } from '@/components/ui/button';
import { answeredCount } from '@/state/experiments/report-questions';
import { cn } from 'helpers/cn';
import { ChipQuestions, TextQuestions, type ReportManager } from './ReportForm';

type AnswersDrawerProps = {
  manager: ReportManager;
  open: boolean;
  onToggle: () => void;
  onCancel: () => void;
  onSave: () => void;
};

/**
 * The answers, under the card. Closed it is a bar saying how many are given; open it is the
 * form, and the card above follows every change. No height animation, in line with the
 * console's motion rule: only colour moves.
 */
export const AnswersDrawer = ({ manager, open, onToggle, onCancel, onSave }: AnswersDrawerProps) => {
  const { isDirty, isSaving, draft } = manager;
  const { answered, total } = answeredCount(draft);

  return (
    <div className="border-line border-t">
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        data-tour="debrief-answers"
        className={cn(
          'hover:bg-hover flex w-full items-center justify-between gap-3 px-3.5 py-3 text-left transition-colors duration-150',
          'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-hidden focus-visible:ring-inset',
          open ? 'bg-panel2' : 'bg-panel',
        )}
      >
        <div className="flex items-center gap-3">
          <span className="micro-label text-foreground">Your answers</span>
          <span className="text-dim font-mono text-[10px]">
            {answered === total ? `all ${total} answered` : `${answered} of ${total} answered`}
          </span>
        </div>
        <span className="supporting">{open ? 'Close' : 'Edit answers'}</span>
      </button>

      {open && (
        <div
          className="border-line grid grid-cols-[repeat(auto-fit,minmax(340px,1fr))] gap-x-8 gap-y-[22px] border-t px-6 pt-5 pb-[22px]"
          data-tour="debrief-questions"
        >
          <div className="flex flex-col gap-[18px]">
            <ChipQuestions manager={manager} />
          </div>

          <div className="flex flex-col gap-[18px]">
            <TextQuestions manager={manager} first="learning" />
            <div className="mt-auto flex items-center justify-end gap-2">
              <span className="text-dim mr-auto font-mono text-[10px]">
                {isDirty ? 'unsaved changes' : 'no changes'}
              </span>
              <Button
                variant="outline"
                onClick={onCancel}
                disabled={isSaving}
                className="border-line text-foreground hover:bg-hover h-8 rounded-lg border bg-transparent px-3 text-meta"
              >
                Cancel
              </Button>
              <Button
                onClick={onSave}
                disabled={!isDirty || isSaving}
                className="bg-cta hover:bg-ctahover h-8 rounded-lg px-3 text-meta text-white"
              >
                {isSaving ? 'Saving…' : 'Save debrief'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
